import { ConfigService } from '../config/config.service';
import { AppStateProvider, mergeIntoState } from '../shared/app_state/AppStateProvider';
import {
    buildSearchString,
    currentUrlParams,
    pushHistoryState,
    replaceHistoryState,
} from '../shared/url_and_history/url_and_history';

const LS_DATA_KEY = 'MainCtrl:Data';

export const URL_STATE_TAG = 'url_param';
export const LS_STATE_TAG = 'local_storage';

function objectFromKeys(keys, sourceObj) {
    return keys.reduce((obj, key) => {
        obj[key] = sourceObj[key];
        return obj;
    }, {});
}

export class MainService {
    _$rootScope;
    _$window;
    _initialLocalStorage;
    _initialUrlParams;
    private _processUrlParamsInProgress: boolean = false;

    state;

    constructor($rootScope, $window, localStorageService) {
        this._$rootScope = $rootScope;
        this._$window = $window;
        this._initialLocalStorage = localStorageService.get(LS_DATA_KEY) || {};
        this._initialUrlParams = currentUrlParams($window) || {};

        this.state = new AppStateProvider({
            effects: {
                handleNewUrlParams: () => ({
                    updateFn: mergeIntoState(
                        objectFromKeys(
                            this.state.keysByTag(URL_STATE_TAG),
                            currentUrlParams($window),
                        ),
                    ),
                    silenceListenersToTag: URL_STATE_TAG,
                }),
            },
        });

        this.state.onChange(
            (state) => {
                // HACK
                if (ConfigService.devMode) {
                    this.tempDevUrlThing();
                } else {
                    const searchStr = buildSearchString(
                        objectFromKeys(this.state.keysByTag(URL_STATE_TAG), state),
                    );
                    pushHistoryState($window, searchStr);
                }
            },
            {
                tags: [URL_STATE_TAG],
            },
        );

        this.state.onChange(
            (state) => {
                localStorageService.set(
                    LS_DATA_KEY,
                    objectFromKeys(this.state.keysByTag(LS_STATE_TAG), state),
                );
            },
            {
                tags: [LS_STATE_TAG],
            },
        );

        $window.onpopstate = async () => {
            if (!this._processUrlParamsInProgress) {
                this._processUrlParamsInProgress = true;
                try {
                    // This will usually return immediately.
                    // Some effects wait for user input to complete.
                    // e.g., pressing back that triggers an asset form close.
                    // The asset may have outstanding changes.
                    // The app waits for user choice to discard the asset changes.
                    await this.state.effects.handleNewUrlParams();
                } catch (err) {
                    // Reverse the url change...
                    $window.history.forward();
                    console.error('Failed to pop state, reverting the url', err);
                } finally {
                    this._processUrlParamsInProgress = false;
                }
            } else {
                // previous pop state processing is in progress. Ignore the last
                // change by reversing the url change...
                $window.history.forward();
            }
        };
    }

    // HACK
    tempDevUrlThing() {
        const searchStr = buildSearchString(
            objectFromKeys(this.state.keysByTag(URL_STATE_TAG), this.state.getState()),
        );
        this._$rootScope.$broadcast('tempDevUrlThing:push', searchStr);
    }

    initialStateValue(key) {
        if (this._initialUrlParams[key] !== undefined) {
            return this._initialUrlParams[key];
        } else if (this._initialLocalStorage[key] !== undefined) {
            return this._initialLocalStorage[key];
        }
    }

    urlParamString() {
        return buildSearchString(
            objectFromKeys(this.state.keysByTag(URL_STATE_TAG), this.state.getState()),
        );
    }

    initialiseUrl() {
        if (ConfigService.devMode) {
            this._$rootScope.$broadcast('tempDevUrlThing:replace', this.urlParamString());
        } else {
            replaceHistoryState(this._$window, this.urlParamString());
        }
    }
}
